import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, X } from 'lucide-react';
import { formatHourRangeCompact } from '@/features/campaigns/utils/campaignUtils';
import { formatChipDate, normalizeDateKey } from '../scheduleStepHelpers';
import { useScheduleSummary } from '../scheduleSummaryContext';
import type { BookingSlot } from '@/features/campaigns/types/campaign';
import { ConfirmAlertDialog } from './ConfirmAlertDialog';

function groupByDate(slots: BookingSlot[]) {
  const map = new Map<string, BookingSlot[]>();
  slots.forEach((s) => {
    const key = normalizeDateKey(s.date);
    const list = map.get(key) || [];
    list.push(s);
    map.set(key, list);
  });
  for (const list of map.values()) {
    list.sort((a, b) => a.hour - b.hour);
  }
  return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
}

/**
 * Schedule summary — right drawer on desktop, bottom sheet on small screens.
 * Replaces the long inline "Your Plan" list.
 */
export function ScheduleSummaryDrawer() {
  const { open, closeSummary, api, totalSlots } = useScheduleSummary();
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [isDesktop, setIsDesktop] = useState(true);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia('(min-width: 768px)');
    const update = () => setIsDesktop(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Let the confirm dialog own Escape while it's open
      if (clearConfirmOpen) return;
      closeSummary();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, closeSummary, clearConfirmOpen]);

  const totalPlays = useMemo(() => {
    if (!api) return 0;
    return api.displays.reduce(
      (sum, d) =>
        sum + d.slots.reduce((acc, s) => acc + (s.repetitions || 1), 0),
      0,
    );
  }, [api]);

  if (!open || typeof document === 'undefined') return null;

  const panel = (
    <div
      className={`flex h-full flex-col bg-white shadow-2xl ${
        isDesktop ? 'w-full max-w-md' : 'max-h-[85vh] rounded-t-2xl'
      }`}
      role="dialog"
      aria-modal="true"
      aria-label="Schedule summary"
    >
      <div className="flex items-start justify-between gap-3 border-b border-[#EEF0F5] px-4 py-3">
        <div>
          <h2 className="m-0 text-[15px] font-semibold text-[#1F2433]">
            Schedule summary
          </h2>
          <p className="m-0 mt-0.5 text-[12px] text-[#6B7289]">
            <span className="font-semibold tabular-nums text-[#1F2433]">
              {totalSlots}
            </span>{' '}
            slots ·{' '}
            <span className="font-semibold tabular-nums text-[#1F2433]">
              {totalPlays}
            </span>{' '}
            plays
          </p>
        </div>
        <button
          type="button"
          onClick={closeSummary}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7289] transition hover:bg-[#F3F4F8] hover:text-[#1F2433]"
          aria-label="Close schedule summary"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3">
        {!api || totalSlots === 0 ? (
          <div className="rounded-xl bg-[#F8F9FB] px-4 py-8 text-center">
            <p className="m-0 text-[13px] font-semibold text-[#1F2433]">
              No slots booked yet
            </p>
            <p className="m-0 mt-1 text-[12px] text-[#6B7289]">
              Select hours in the inventory grid to build your schedule.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {api.displays.map((display) => {
              if (display.slots.length === 0) return null;
              const plays = display.slots.reduce(
                (acc, s) => acc + (s.repetitions || 1),
                0,
              );
              const byDate = groupByDate(display.slots);

              return (
                <section key={display.id} className="text-left">
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3
                        className="m-0 truncate text-[13px] font-semibold text-[#1F2433]"
                        title={display.name}
                      >
                        {display.name}
                      </h3>
                      <p className="m-0 mt-0.5 text-[11px] text-[#6B7289]">
                        {display.slots.length} slots · {plays} plays
                        {display.orientation
                          ? ` · ${display.orientation}`
                          : ''}
                      </p>
                    </div>
                    {api.onClearDisplay ? (
                      <button
                        type="button"
                        onClick={() => api.onClearDisplay?.(display.id)}
                        className="shrink-0 text-[11px] font-semibold text-[#6B7289] transition hover:text-rose-600"
                      >
                        Clear
                      </button>
                    ) : null}
                  </div>

                  <div className="space-y-2">
                    {byDate.map(([date, list]) => {
                      const key = `${display.id}:${date}`;
                      const isCollapsed = collapsed[key];
                      const label = formatChipDate(date);
                      return (
                        <div
                          key={key}
                          className="rounded-lg border border-[#E6E8EF] bg-[#F8F9FB]"
                        >
                          <button
                            type="button"
                            onClick={() =>
                              setCollapsed((prev) => ({
                                ...prev,
                                [key]: !prev[key],
                              }))
                            }
                            className="flex w-full items-center justify-between gap-2 px-2.5 py-2 text-left"
                          >
                            <span className="text-[12px] font-semibold text-[#1F2433]">
                              {label.weekday} · {label.dayMonth}
                              <span className="ml-1.5 font-medium text-[#6B7289]">
                                ({list.length})
                              </span>
                            </span>
                            <ChevronDown
                              className={`h-3.5 w-3.5 text-[#6B7289] transition ${
                                isCollapsed ? '-rotate-90' : ''
                              }`}
                            />
                          </button>

                          {!isCollapsed ? (
                            <div className="flex flex-wrap gap-1.5 border-t border-[#E6E8EF] px-2.5 py-2">
                              {list.map((s) => (
                                <span
                                  key={`${s.date}-${s.hour}`}
                                  className="inline-flex items-center gap-1 rounded-md border border-[#E6E8EF] bg-white py-0.5 pr-0.5 pl-2 text-[11px] text-[#1F2433]"
                                >
                                  <span className="font-medium">
                                    {formatHourRangeCompact(s.hour)}
                                  </span>
                                  <span className="text-[#6B7289]">
                                    · {s.repetitions}×
                                  </span>
                                  <button
                                    type="button"
                                    aria-label="Remove booking"
                                    onClick={() =>
                                      api.onRemoveSlot(
                                        display.id,
                                        s.date,
                                        s.hour,
                                      )
                                    }
                                    className="flex h-5 w-5 items-center justify-center rounded text-[#9CA3AF] transition hover:bg-[#F3F4F8] hover:text-rose-600"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>

      {api?.onClearAll && totalSlots > 0 ? (
        <div className="border-t border-[#EEF0F5] px-4 py-3">
          <button
            type="button"
            onClick={() => setClearConfirmOpen(true)}
            className="w-full rounded-lg border border-[#E6E8EF] py-2 text-[12px] font-semibold text-rose-600 transition hover:bg-rose-50"
          >
            Clear entire schedule
          </button>
        </div>
      ) : null}
    </div>
  );

  return createPortal(
    <>
    <div className="fixed inset-0 z-[100] flex" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"
        aria-label="Close schedule summary"
        onClick={closeSummary}
      />
      <div
        className={`relative z-10 ml-auto flex ${
          isDesktop ? 'h-full' : 'mt-auto w-full'
        }`}
      >
        {panel}
      </div>
    </div>
    <ConfirmAlertDialog
      open={clearConfirmOpen}
      title="Clear entire schedule?"
      description={`This removes all ${totalSlots} booked slot${totalSlots === 1 ? '' : 's'} across every display. This cannot be undone.`}
      confirmLabel="Clear schedule"
      cancelLabel="Keep schedule"
      tone="danger"
      onCancel={() => setClearConfirmOpen(false)}
      onConfirm={() => {
        api?.onClearAll?.();
        setClearConfirmOpen(false);
      }}
    />
    </>,
    document.body,
  );
}
